// @flow

import React, { type Node, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useUniqueId } from '@tectonics/hooks';
import { useUserInputAsTextFieldWithFormatting } from '@tectonics/prs/user-input-values';
import { useUserInputValidationError, type UserInputValidationError, serverErrorMessage } from '@tectonics/prs/validation';
import { useSubmitting } from '@tectonics/prs/submitting';
import { getZipcodeNormalizer } from '@tectonics/prs/core/customer/normalize';
import { useIncomingPayment, getCountry } from '@tectonics/prs/incoming-payment';
import { useTranslation } from 'react-i18next';

import { TextField } from '../../../../../atoms';
import { ErrorLabel } from '../../../../../molecules/error-label';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';

import { InputMask, formatValueWithMask, type ArrayMask } from '@tectonics/components/input-mask';
import { useCurrentPaymentTypeOptionalFields } from '@tectonics/prs/incoming-payment/get-payment-type-optional-fields';

type Props = {|
  fieldName: string,
|};

export function FieldZipcode({ fieldName }: Props): Node {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);
  const zipcodeNormalizer = useMemo(() => getZipcodeNormalizer(country), [country]);
  const inputId = useUniqueId();
  const submitting = useSubmitting();
  const mask = getMaskByCountry(country);
  const formatRaw = useCallback((rawValue: string) => formatValueWithMask(rawValue, mask), [mask]);

  const [value, handleChange] = useUserInputAsTextFieldWithFormatting(
    fieldName,
    formatRaw,
    zipcodeNormalizer,
  );

  const validationError = useUserInputValidationError(fieldName);
  const formHasBeenSent = useFormHasBeenSent();
  const isOptionalFieldByPaymentType = useCurrentPaymentTypeOptionalFields('zipcode');

  if (isOptionalFieldByPaymentType) {
    return null;
  }

  return (
    <div data-test-name="address-zipcode">
      <LabelWrapper>
        <label htmlFor={inputId}>
          <ZipcodeLabel />
        </label>
      </LabelWrapper>

      <TextField
        type="tel"
        as={InputMask}
        id={inputId}
        mask={mask}
        value={value}
        onChange={handleChange}
        fashion={formHasBeenSent && validationError ? 'error' : null}
        readOnly={submitting}
        data-error={formHasBeenSent && validationError ? 'error' : null}
        name="address-zipcode"
      />

      {formHasBeenSent && validationError && (
        <ZipcodeValidationMessage validationError={validationError} />
      )}
    </div>
  );
}

export const LabelWrapper = styled.div`
  display: flex;
  margin-bottom: 4px;
  label {
    flex-grow: 1;
  }
`;

type ZipcodeValidationMessageProps = {|
  validationError: UserInputValidationError,
|};

function ZipcodeValidationMessage({ validationError }: ZipcodeValidationMessageProps): Node {
  const { t } = useTranslation('opened-page');

  switch (validationError.id) {
    case 'empty':
      return (
        <ErrorLabel>
          {t('volcanes.form.zipcode.errorEmpty')}
        </ErrorLabel>
      );

    case 'server-error':
      return (
        <ErrorLabel>
          {serverErrorMessage(validationError.payload)}
        </ErrorLabel>
      );

    default:
      return (
        <ErrorLabel>
          {t('volcanes.form.zipcode.errorInvalid')}
        </ErrorLabel>
      );
  }
}

function ZipcodeLabel(): Node {
  const { t } = useTranslation('opened-page');
  return t('volcanes.form.zipcode.label');
}

function getMaskByCountry(country: string): ArrayMask {
  switch (country){
    case 'co':
      return(COLOMBIAN_MASK);

    case 'pe':
      return(PERUVIAN_MASK);

    case 'cl':
      return(CHILEAN_MASK);

    case 'uy':
      return(URUGUAYAN_MASK);

    case 'cr':
      return(COSTA_RICAN_MASK);

    case 'py':
      return(PARAGUAYAN_MASK);

    case 'pa':
      return(PANAMANIAN_MASK);

    case 'sv':
      return(EL_SALVADOR_MASK);

    case 'gt':
      return(GUATEMALAN_MASK);

    case 'do':
      return(DOMINICAN_MASK);

    case 'za':
      return(SOUTH_AFRICAN_MASK);

    default:
      throw new Error(`Field Zipcode: mask not mapped for ${country}`);
  }
}

const COLOMBIAN_MASK = [
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
];

const PERUVIAN_MASK = [
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
];

const CHILEAN_MASK = [
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
];

const URUGUAYAN_MASK = [
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
];

const PARAGUAYAN_MASK = [
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
];

const COSTA_RICAN_MASK = [
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
];

const PANAMANIAN_MASK = [
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
];

const GUATEMALAN_MASK = [
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
];

const EL_SALVADOR_MASK = [
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
];

const DOMINICAN_MASK = [
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
];

const SOUTH_AFRICAN_MASK = [
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
];
